import { FunctionalComponent } from 'preact'
import type { CompanyTrackingViewDto } from '@/types/tracking'
import type { ApiResponse } from '@/api/client'
import type { CustomScriptFields } from '@/hooks/useCompanyTracking'
import { TrackingSnippet } from './TrackingSnippet'
import { TrackingToggle } from './TrackingToggle'
import { CustomTrackingScriptForm } from './CustomTrackingScriptForm'

interface VisitorTrackingPanelProps {
  tracking: CompanyTrackingViewDto | null
  isLoading: boolean
  error: string | null
  setEnabled: (next: boolean) => Promise<void>
  saveScripts: (scripts: CustomScriptFields) => Promise<ApiResponse<CompanyTrackingViewDto> | null>
}

export const VisitorTrackingPanel: FunctionalComponent<VisitorTrackingPanelProps> = ({
  tracking,
  isLoading,
  error,
  setEnabled,
  saveScripts,
}) => {
  return (
    <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 p-6">
      <div className="flex items-start justify-between gap-4 mb-4">
        <div>
          <h3 className="text-lg font-semibold text-slate-900 dark:text-white">Visitor Tracking</h3>
          <p className="text-sm text-slate-600 dark:text-slate-300 mt-1">
            Install this snippet on your site to capture visitor activity for this company.
          </p>
        </div>
        {tracking && (
          <div className="flex-shrink-0">
            <TrackingToggle
              enabled={tracking.Enabled}
              onChange={(next) => void setEnabled(next)}
            />
          </div>
        )}
      </div>

      {isLoading && (
        <div className="space-y-3">
          <div className="h-4 w-32 rounded bg-slate-200 dark:bg-slate-600 animate-pulse" />
          <div className="h-20 rounded bg-slate-200 dark:bg-slate-600 animate-pulse" />
        </div>
      )}

      {!isLoading && error && (
        <div className="text-sm text-red-600 dark:text-red-400">{error}</div>
      )}

      {!isLoading && !error && tracking && (
        <div className="space-y-4">
          <div>
            <div className="text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400 mb-1">
              Tracking ID
            </div>
            <div className="font-mono text-sm text-slate-800 dark:text-slate-100 break-all">
              {tracking.TrackingId}
            </div>
          </div>

          <div>
            <div className="text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400 mb-1">
              Install Snippet
            </div>
            <TrackingSnippet
              TrackingId={tracking.TrackingId}
              ScriptUrl={tracking.ScriptUrl}
              label="Company tracking snippet"
            />
          </div>

          <div className="border-t border-slate-200 dark:border-slate-700 pt-4">
            <CustomTrackingScriptForm tracking={tracking} onSave={saveScripts} />
          </div>
        </div>
      )}
    </div>
  )
}
